/* Purchase history UI (client, vanilla ES6) — 2026-06 brand redesign.
   Consumes /apps/wth-admin/orders/data, /orders/:id/data, /orders/insights,
   /tracking and /reorder (Shopify App Proxy).
   Hash routing: `#/order/<numericId>` = detail view, else list view.

   Redesign: navy header, KPI strip, "to track" callout (surfaces in-transit +
   preparing orders; 1 order → its detail, several → highlight them in the list),
   order cards with an item summary + 3-step mini timeline + always-visible
   Details/Reorder actions, collapsible insights, and a detail order status
   timeline whose Delivered step is upgraded live from FedEx tracking (the order
   payload carries NO delivery timestamp — delivery is only knowable live). */

(function () {
  'use strict';

  /* Decode HTML entities — the Liquid `t` filter HTML-escapes its output, so
     i18n values injected via `| json` arrive with &#39; etc. Decoding once at
     load makes every textContent assignment render correctly (and escHtml
     re-escapes safely for innerHTML paths). */
  function wthDecodeEntities(s) {
    return String(s)
      .replace(/&#39;/g, "'").replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // --- Inline SVG icons (reused across renders) ---
  var SVG = {
    tire: '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/></svg>',
    truck: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M14 9h4l4 4v4a1 1 0 0 1-1 1h-1"/><circle cx="7.5" cy="18.5" r="2.5"/><circle cx="17.5" cy="18.5" r="2.5"/></svg>',
    check: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>',
    cart: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18M16 10a4 4 0 0 1-8 0"/></svg>',
    box: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 8a2 2 0 0 0-1-1.73L13 2.27a2 2 0 0 0-2 0L4 6.27A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5M12 22V12"/></svg>',
    truckSmall: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M14 9h4l4 4v4a1 1 0 0 1-1 1h-1"/><circle cx="7.5" cy="18.5" r="2.5"/><circle cx="17.5" cy="18.5" r="2.5"/></svg>',
    home: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>',
    x: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/></svg>',
    reorder: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 2v6h6"/><path d="M3 13a9 9 0 1 0 3-7.7L3 8"/></svg>',
    invoice: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h6"/></svg>',
    trackLine: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M14 9h4l4 4v4a1 1 0 0 1-1 1h-1"/><circle cx="7.5" cy="18.5" r="2.5"/><circle cx="17.5" cy="18.5" r="2.5"/></svg>'
  };
  // step key → icon for the not-yet-reached state
  var STEP_ICON = { ordered: SVG.cart, preparing: SVG.box, shipped: SVG.truckSmall, delivered: SVG.home };

  onReady(function () {
    var root = document.querySelector('[data-purchase-history]');
    if (!root) return;

    // --- i18n (decode entities on load) ---
    var i18n = {};
    try {
      var i18nEl = document.querySelector('[data-wth-orders-i18n]');
      if (i18nEl && i18nEl.textContent) {
        i18n = JSON.parse(i18nEl.textContent) || {};
      }
    } catch (e) {
      i18n = {};
    }
    Object.keys(i18n).forEach(function (k) {
      if (typeof i18n[k] === 'string') i18n[k] = wthDecodeEntities(i18n[k]);
    });

    /* Shorthand translate helper. Replaces `__VAR__` tokens with provided values. */
    function t(key, vars) {
      var raw = i18n[key];
      if (typeof raw !== 'string') return key;
      if (!vars) return raw;
      return raw.replace(/__([A-Z_]+)__/g, function (_m, name) {
        var v = vars[name.toLowerCase()];
        if (v === undefined) v = vars[name];
        return v === undefined || v === null ? '' : String(v);
      });
    }

    var isFrench = /^fr/i.test(i18n.locale || '');

    // --- Module state ---
    var state = {
      orders: null,
      customer: null,
      listError: '',
      detailOrder: null,
      detailError: '',
      detailId: null,
      filters: { orderOrPo: '', productSkuOrName: '', status: '', dateFrom: '', dateTo: '', preset: '' },
      sort: 'date-desc',
      perPage: 20,
      currentPage: 1,
      reordering: null,
      pageInfo: null,
      loadingMore: false,
      loadMoreError: false,
      trackable: [],   // orders surfaced by the "to track" callout
    };

    var listView = root.querySelector('[data-view="list"]');
    var detailView = root.querySelector('[data-view="detail"]');
    if (!listView || !detailView) return;

    // --- Formatting helpers ---
    function formatMoney(amount, currency) {
      var n = parseFloat(amount);
      if (isNaN(n)) return amount + ' ' + currency;
      try {
        return new Intl.NumberFormat(isFrench ? 'fr-CA' : 'en-CA', { style: 'currency', currency: currency || 'CAD' }).format(n);
      } catch (e) {
        return '$' + n.toFixed(2);
      }
    }
    function formatMoneyObj(m) { return !m ? '—' : formatMoney(m.amount, m.currencyCode); }
    function formatDate(iso) {
      if (!iso) return '—';
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleDateString(isFrench ? 'fr-CA' : 'en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    function formatDateTime(iso) {
      if (!iso) return '—';
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleString(isFrench ? 'fr-CA' : 'en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }

    function statusInfoForSummary(order) {
      if (order.cancelledAt) return { label: t('status_cancelled'), className: 'wth-oh3__pill-cancelled', kind: 'cancelled' };
      var f = (order.fulfillmentStatus || '').toUpperCase();
      if (f === 'FULFILLED') return { label: t('status_shipped'), className: 'wth-oh3__pill-shipped', kind: 'shipped' };
      if (f === 'PARTIALLY_FULFILLED' || f === 'PARTIAL') return { label: t('status_partial'), className: 'wth-oh3__pill-partial', kind: 'partial' };
      return { label: t('status_pending'), className: 'wth-oh3__pill-pending', kind: 'pending' };
    }
    function statusInfoForDetail(order) {
      if (order.cancelledAt) return { label: t('status_cancelled'), className: 'wth-oh3__pill-cancelled' };
      var f = (order.displayFulfillmentStatus || '').toUpperCase();
      if (f === 'FULFILLED') return { label: t('status_shipped'), className: 'wth-oh3__pill-shipped' };
      if (f === 'PARTIALLY_FULFILLED' || f === 'PARTIAL') return { label: t('status_partial'), className: 'wth-oh3__pill-partial' };
      return { label: t('status_pending'), className: 'wth-oh3__pill-pending' };
    }

    function rangeFromPreset(preset) {
      var now = new Date();
      var to = now.toISOString().slice(0, 10);
      if (preset === 'ytd') return { from: now.getFullYear() + '-01-01', to: to };
      var days = preset === '7' ? 7 : preset === '30' ? 30 : preset === '90' ? 90 : 0;
      if (!days) return { from: '', to: '' };
      var d = new Date(now.getTime() - days * 86400000);
      return { from: d.toISOString().slice(0, 10), to: to };
    }

    function escHtml(s) {
      if (s == null) return '';
      var d = document.createElement('div');
      d.textContent = String(s);
      return d.innerHTML;
    }
    function escAttr(s) { return escHtml(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;'); }

    function buildLoginUrl() {
      var returnTo = window.location.pathname + window.location.search;
      if (typeof window.WTHLoginUrl === 'function') return window.WTHLoginUrl(returnTo);
      var base = i18n.account_login_url || '/account/login';
      if (typeof window.WTHStripReturnTo === 'function') base = window.WTHStripReturnTo(base);
      return base + (base.indexOf('?') === -1 ? '?' : '&') + 'return_to=' + encodeURIComponent(returnTo);
    }

    function showToast(message, variant) {
      if (typeof window.WTHCartToastShow === 'function') { window.WTHCartToastShow(message, variant || 'error'); return; }
      window.alert(message);
    }

    function cleanProductName(s) {
      if (s == null) return '';
      return String(s)
        .split(/[\r\n\u2028\u2029\u0085]/)
        .map(function (p) { return p.trim(); })
        .filter(function (p) { return p && p !== '-' && p !== '—'; })
        .join(' · ');
    }

    function resolveImageUrl(item) {
      if (!item) return null;
      if (item.imageUrl) return item.imageUrl;
      if (item.image && item.image.url) return item.image.url;
      if (item.variant) {
        if (item.variant.image && item.variant.image.url) return item.variant.image.url;
        if (item.variant.product && item.variant.product.featuredImage && item.variant.product.featuredImage.url) {
          return item.variant.product.featuredImage.url;
        }
      }
      return null;
    }

    function liRow(name, sku, quantity, imageUrl, priceLabel) {
      var thumb = imageUrl
        ? '<img class="wth-oh3__li-img" src="' + escAttr(imageUrl) + '" alt="" loading="lazy">'
        : '<div class="wth-oh3__li-img wth-oh3__li-img--placeholder" aria-hidden="true"></div>';
      var skuHtml = sku ? '<div class="wth-oh3__li-sku">' + escHtml(sku) + '</div>' : '';
      var priceHtml = priceLabel
        ? '<div class="wth-oh3__li-price">' + escHtml(priceLabel) + '</div>'
        : '<div class="wth-oh3__li-price"></div>';
      return ''
        + '<div class="wth-oh3__li">'
        + thumb
        + '<div class="wth-oh3__li-body">'
        + '<div class="wth-oh3__li-title">' + escHtml(cleanProductName(name)) + '</div>'
        + skuHtml
        + '</div>'
        + '<div class="wth-oh3__li-qty">× ' + quantity + '</div>'
        + priceHtml
        + '</div>';
    }

    var STATUS_I18N_KEYS = {
      FULFILLED: 'status_shipped', PARTIALLY_FULFILLED: 'status_partial', UNFULFILLED: 'status_pending',
      PENDING_FULFILLMENT: 'status_pending', RESTOCKED: 'status_restocked', SCHEDULED: 'status_scheduled',
      ON_HOLD: 'status_on_hold', PAID: 'status_paid', PARTIALLY_PAID: 'status_partially_paid',
      PENDING: 'status_pending', REFUNDED: 'status_refunded', PARTIALLY_REFUNDED: 'status_partial_refunded',
      VOIDED: 'status_voided', AUTHORIZED: 'status_authorized', EXPIRED: 'status_expired', OPEN: 'status_open',
    };
    function prettyStatus(raw) {
      if (!raw) return '';
      var key = String(raw).toUpperCase();
      var i18nKey = STATUS_I18N_KEYS[key];
      if (i18nKey && typeof i18n[i18nKey] === 'string') return i18n[i18nKey];
      var s = String(raw).toLowerCase().replace(/_/g, ' ');
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
    function formatPhone(raw) {
      if (!raw) return '';
      var s = String(raw).trim();
      var m = s.replace(/[^\d+]/g, '').match(/^\+?1?(\d{3})(\d{3})(\d{4})$/);
      if (m) return '(' + m[1] + ') ' + m[2] + '-' + m[3];
      return s;
    }
    function prettyFulfillmentStatus(raw) {
      if (!raw) return t('status_pending');
      var s = String(raw).toUpperCase();
      if (s === 'SUCCESS' || s === 'OPEN' || s === 'IN_PROGRESS') return t('status_shipped');
      if (s === 'CANCELLED') return t('status_cancelled');
      if (s === 'ERROR' || s === 'FAILURE') return t('status_failed');
      return prettyStatus(raw);
    }

    // --- Data fetching ---
    function fetchOrders(after) {
      var qs = window.location.search || '';
      var url = '/apps/wth-admin/orders/data' + qs;
      if (after) url += (qs ? '&' : '?') + 'after=' + encodeURIComponent(after);
      return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json().then(function (json) { return { status: res.status, json: json }; }); })
        .then(function (r) {
          if (r.status === 401 || r.status === 403) {
            state.listError = ''; state.orders = null; state.customer = null; state.pageInfo = null; return;
          }
          if (r.status !== 200) {
            if (after) { state.loadMoreError = true; return; }
            state.listError = t('error_load_orders'); state.orders = []; state.customer = null; return;
          }
          if (!r.json.signedIn) {
            state.listError = ''; state.orders = null; state.customer = null; state.pageInfo = null; return;
          }
          state.listError = '';
          var fetched = r.json.orders || [];
          state.orders = after && state.orders ? state.orders.concat(fetched) : fetched;
          state.customer = r.json.customer || null;
          state.pageInfo = r.json.page_info || null;
        })
        .catch(function () {
          if (after) { state.loadMoreError = true; return; }
          state.listError = t('error_network'); state.orders = [];
        });
    }

    function fetchOrder(numericId) {
      var url = '/apps/wth-admin/orders/' + encodeURIComponent(numericId) + '/data' + (window.location.search || '');
      return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json().then(function (json) { return { status: res.status, json: json }; }); })
        .then(function (r) {
          if (r.status === 403 && r.json && /access/i.test(r.json.error || '')) {
            state.detailError = t('error_no_access'); state.detailOrder = null; return;
          }
          if (r.status === 401 || r.status === 403) { state.detailError = ''; state.detailOrder = null; return; }
          if (r.status === 404) { state.detailError = t('error_not_found'); state.detailOrder = null; return; }
          if (r.status !== 200) { state.detailError = t('error_load_order'); state.detailOrder = null; return; }
          if (!r.json.signedIn) { state.detailError = ''; state.detailOrder = null; return; }
          state.detailError = ''; state.detailOrder = r.json.order || null;
        })
        .catch(function () { state.detailError = t('error_network'); state.detailOrder = null; });
    }

    // --- Routing ---
    function parseHash() {
      var m = (window.location.hash || '').match(/^#\/order\/(\d+)$/);
      return m ? m[1] : null;
    }
    function showList() {
      listView.hidden = false; detailView.hidden = true;
      if (state.orders === null && !state.listError) {
        renderListLoading();
        fetchOrders().then(function () { renderList(); maybeLoadInsights(); });
      } else {
        renderList(); maybeLoadInsights();
      }
    }
    function showDetail(numericId) {
      listView.hidden = true; detailView.hidden = false;
      state.detailId = numericId;
      renderDetailLoading();
      fetchOrder(numericId).then(renderDetail);
    }
    function route() {
      var id = parseHash();
      if (id) showDetail(id); else showList();
    }
    window.addEventListener('hashchange', route);

    // --- Filtering / sorting ---
    function filterOrders(orders) {
      var needleA = (state.filters.orderOrPo || '').trim().toLowerCase();
      var needleB = (state.filters.productSkuOrName || '').trim().toLowerCase();
      var fromTs = state.filters.dateFrom ? new Date(state.filters.dateFrom + 'T00:00:00').getTime() : null;
      var toTs = state.filters.dateTo ? new Date(state.filters.dateTo + 'T23:59:59').getTime() : null;
      return orders.filter(function (o) {
        if (needleA) {
          var combined = (o.name + ' ' + (o.poNumber || '')).toLowerCase();
          if (combined.indexOf(needleA) === -1) return false;
        }
        if (needleB) {
          var hit = o.lineItems.some(function (li) { return (li.name + ' ' + (li.sku || '')).toLowerCase().indexOf(needleB) !== -1; });
          if (!hit) return false;
        }
        if (state.filters.status) {
          if (statusInfoForSummary(o).kind !== state.filters.status) return false;
        }
        if (fromTs !== null || toTs !== null) {
          var ts2 = o.processedAt ? new Date(o.processedAt).getTime() : null;
          if (ts2 === null) return false;
          if (fromTs !== null && ts2 < fromTs) return false;
          if (toTs !== null && ts2 > toTs) return false;
        }
        return true;
      });
    }
    function sortOrders(orders) {
      var copy = orders.slice();
      var sort = state.sort;
      copy.sort(function (a, b) {
        if (sort.indexOf('date') === 0) {
          var ta = a.processedAt ? new Date(a.processedAt).getTime() : 0;
          var tb = b.processedAt ? new Date(b.processedAt).getTime() : 0;
          return sort === 'date-desc' ? tb - ta : ta - tb;
        }
        var na = parseFloat(a.total.amount) || 0;
        var nb = parseFloat(b.total.amount) || 0;
        return sort === 'total-desc' ? nb - na : na - nb;
      });
      return copy;
    }

    // --- KPI strip + to-track callout ---
    function orderItemCount(order, skuOnly) {
      return (order.lineItems || []).reduce(function (s, li) {
        return s + ((skuOnly && !li.sku) ? 0 : li.quantity);
      }, 0);
    }

    function renderKpis(all) {
      var year = new Date().getFullYear();
      var ytdSpend = 0, ytdItems = 0, openCount = 0, firstYear = year;
      for (var i = 0; i < all.length; i++) {
        var o = all[i];
        var oy = o.processedAt ? new Date(o.processedAt).getFullYear() : null;
        if (oy !== null && oy < firstYear) firstYear = oy;
        if (oy === year) { ytdSpend += parseFloat(o.total.amount) || 0; ytdItems += orderItemCount(o, true); }
        var k = statusInfoForSummary(o).kind;
        if (k === 'pending' || k === 'partial') openCount++;
      }
      var cur = (all[0] && all[0].total.currency) || 'CAD';
      function set(name, val, sub) {
        var v = listView.querySelector('[data-kpi="' + name + '"]');
        var s = listView.querySelector('[data-kpi-sub="' + name + '"]');
        if (v) v.textContent = val;
        if (s) s.textContent = sub;
      }
      set('orders', String(all.length), t('kpi_since', { year: firstYear }));
      set('spent', formatMoney(ytdSpend.toFixed(2), cur), t('kpi_in_year', { year: year }));
      set('open', String(openCount), openCount > 0 ? t('kpi_to_track') : t('kpi_all_caught_up'));
      set('items', String(ytdItems), t('kpi_in_year', { year: year }));
    }

    /* Orders worth surfacing for tracking: open (pending/partial) always, plus
       recently shipped (≤7d — delivery isn't knowable from the list payload, so
       a tighter window avoids surfacing orders that already arrived). */
    function computeTrackable(all) {
      var cutoff = Date.now() - 7 * 86400000;
      return all.filter(function (o) {
        var k = statusInfoForSummary(o).kind;
        if (k === 'pending' || k === 'partial') return true;
        if (k === 'shipped') {
          var ts = o.processedAt ? new Date(o.processedAt).getTime() : 0;
          return ts >= cutoff;
        }
        return false;
      });
    }

    function renderCallout(all) {
      var el = listView.querySelector('[data-track-callout]');
      if (!el) return;
      var trackable = computeTrackable(all);
      state.trackable = trackable.map(function (o) { return { id: o.id, numericId: o.numericId }; });
      if (trackable.length === 0) { el.innerHTML = ''; return; }
      var heading = trackable.length === 1 ? t('track_callout_heading_one') : t('track_callout_heading', { count: trackable.length });
      var cta = trackable.length === 1 ? t('track_callout_cta_one') : t('track_callout_cta');
      el.innerHTML = ''
        + '<div class="wth-oh3__callout">'
        + '<div class="wth-oh3__callout-ic">' + SVG.truck + '</div>'
        + '<div class="wth-oh3__callout-txt"><b>' + escHtml(heading) + '</b><p>' + escHtml(t('track_callout_sub')) + '</p></div>'
        + '<button type="button" class="wth-oh3__callout-cta" data-track-go>' + escHtml(cta) + '</button>'
        + '</div>';
    }

    function flashOrders(ids) {
      var first = null;
      ids.forEach(function (id) {
        var sel = (window.CSS && CSS.escape) ? CSS.escape(id) : id.replace(/"/g, '\\"');
        var card = listView.querySelector('[data-order-card="' + sel + '"]');
        if (!card) return;
        if (!first) first = card;
        card.classList.add('wth-oh3__order-card--flash');
        setTimeout(function () { card.classList.remove('wth-oh3__order-card--flash'); }, 2400);
      });
      // None of the trackable cards are on the current page (e.g. an old pending
      // order pushed past page 1) — fall back to scrolling the orders region.
      if (!first) first = listView.querySelector('[data-orders]');
      if (first && first.scrollIntoView) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function handleTrackPackages() {
      var list = state.trackable || [];
      if (list.length === 0) return;
      if (list.length === 1) { window.location.hash = '#/order/' + encodeURIComponent(list[0].numericId); return; }
      // Several packages: reset filters + sort so they surface, then highlight them.
      state.filters = { orderOrPo: '', productSkuOrName: '', status: '', dateFrom: '', dateTo: '', preset: '' };
      state.sort = 'date-desc';
      state.currentPage = 1;
      renderList();
      flashOrders(list.map(function (o) { return o.id; }));
    }

    // --- Item summary + mini timeline ---
    function itemSummary(order) {
      var items = order.lineItems || [];
      var real = items.filter(function (li) { return li.sku; });
      if (real.length === 0) real = items.slice();
      if (real.length === 0) return { lead: '', sub: '' };
      var first = real[0];
      var lead = first.quantity + '× ' + cleanProductName(first.name);
      var sub;
      if (real.length > 1) sub = (real.length - 1 === 1) ? t('card_more_one') : t('card_more', { count: real.length - 1 });
      else sub = first.sku || '';
      return { lead: lead, sub: sub };
    }

    function miniTimelineHtml(kind) {
      var shipped = kind === 'shipped' || kind === 'partial';
      var s1 = shipped ? ' is-done' : '';
      var n2 = shipped ? ' is-done' : '';
      return '<span class="wth-oh3__mini-tl" aria-hidden="true">'
        + '<span class="wth-oh3__mini-node is-done"></span>'
        + '<span class="wth-oh3__mini-seg' + s1 + '"></span>'
        + '<span class="wth-oh3__mini-node' + n2 + '"></span>'
        + '<span class="wth-oh3__mini-seg"></span>'
        + '<span class="wth-oh3__mini-node"></span>'
        + '</span>';
    }

    // --- List render ---
    function renderListLoading() {
      var kpis = listView.querySelector('[data-kpis]');
      var filters = listView.querySelector('[data-filters]');
      var results = listView.querySelector('[data-results]');
      var ordersEl = listView.querySelector('[data-orders]');
      var pagination = listView.querySelector('[data-pagination]');
      if (kpis) kpis.style.display = '';
      if (filters) filters.style.display = '';
      if (results) results.textContent = t('loading');
      if (ordersEl) ordersEl.innerHTML = '<div class="wth-oh3__loading">' + escHtml(t('loading_orders')) + '</div>';
      if (pagination) pagination.innerHTML = '';
    }

    function renderList() {
      // Sign-in required?
      if (state.orders === null) {
        var loginUrl = buildLoginUrl();
        listView.innerHTML = ''
          + '<div class="wth-oh3__empty">'
          + '<h3 class="wth-oh3__heading">' + escHtml(t('please_sign_in_heading')) + '</h3>'
          + '<p>' + escHtml(t('please_sign_in_body')) + '</p>'
          + '<p><a class="wth-oh3__cta-btn" href="' + escAttr(loginUrl) + '">' + escHtml(t('sign_in_cta')) + '</a></p>'
          + '</div>';
        return;
      }
      if (state.listError) {
        listView.innerHTML = '<div class="wth-oh3__error-card">' + escHtml(state.listError) + '</div>';
        return;
      }

      renderSubtitle();

      var all = state.orders;
      var noOrders = all.length === 0;

      var calloutEl = listView.querySelector('[data-track-callout]');
      var kpisEl = listView.querySelector('[data-kpis]');
      var filtersEl = listView.querySelector('[data-filters]');
      var resultsBarEl = listView.querySelector('[data-results-bar]');
      if (kpisEl) kpisEl.style.display = noOrders ? 'none' : '';
      if (filtersEl) filtersEl.style.display = noOrders ? 'none' : '';
      if (resultsBarEl) resultsBarEl.style.display = noOrders ? 'none' : '';
      if (calloutEl && noOrders) { calloutEl.innerHTML = ''; state.trackable = []; }

      var filtered = filterOrders(all);
      var sorted = sortOrders(filtered);
      var totalPages = Math.max(1, Math.ceil(sorted.length / state.perPage));
      if (state.currentPage > totalPages) state.currentPage = totalPages;
      var pageStart = (state.currentPage - 1) * state.perPage;
      var pageEnd = Math.min(pageStart + state.perPage, sorted.length);
      var pageItems = sorted.slice(pageStart, pageEnd);

      if (!noOrders) { renderKpis(all); renderCallout(all); }

      syncFilterInputs();

      // Partial-data note when older orders remain on the server.
      var noteEl = listView.querySelector('[data-stats-note]');
      if (noteEl) {
        var hasMore = !!(state.pageInfo && state.pageInfo.has_next);
        if (hasMore && !noOrders) { noteEl.textContent = t('stats_partial_note', { count: all.length }); noteEl.hidden = false; }
        else noteEl.hidden = true;
      }

      var resultsEl = listView.querySelector('[data-results]');
      if (resultsEl) {
        if (sorted.length === 0) resultsEl.textContent = t('results_none');
        else resultsEl.textContent = t('results_showing', { from: pageStart + 1, to: pageEnd, total: sorted.length });
      }
      announceResultCount(sorted.length);

      var ordersEl = listView.querySelector('[data-orders]');
      if (!ordersEl) return;
      if (noOrders) {
        ordersEl.innerHTML = ''
          + '<div class="wth-oh3__empty">'
          + '<h3 class="wth-oh3__heading">' + escHtml(t('empty_no_orders_heading')) + '</h3>'
          + '<p>' + escHtml(t('empty_no_orders_body')) + '</p>'
          + '<p><a class="wth-oh3__cta-btn" href="/">' + escHtml(t('empty_no_orders_cta')) + '</a></p>'
          + '</div>';
      } else if (sorted.length === 0) {
        ordersEl.innerHTML = '<div class="wth-oh3__empty">' + escHtml(t('empty_filtered')) + '</div>';
      } else {
        ordersEl.innerHTML = pageItems.map(renderOrderCard).join('');
      }

      var pagEl = listView.querySelector('[data-pagination]');
      if (pagEl) {
        var pagHtml = '';
        if (sorted.length > state.perPage) {
          pagHtml += ''
            + '<button type="button" class="wth-oh3__paginate-btn" data-page-prev' + (state.currentPage <= 1 ? ' disabled' : '') + '>' + escHtml(t('paginate_prev')) + '</button>'
            + '<span>' + escHtml(t('paginate_page_of', { current: state.currentPage, total: totalPages })) + '</span>'
            + '<button type="button" class="wth-oh3__paginate-btn" data-page-next' + (state.currentPage >= totalPages ? ' disabled' : '') + '>' + escHtml(t('paginate_next')) + '</button>';
        }
        if (state.pageInfo && state.pageInfo.has_next) {
          pagHtml += '<button type="button" class="wth-oh3__paginate-btn wth-oh3__load-more" data-load-more' + (state.loadingMore ? ' disabled' : '') + '>'
            + escHtml(state.loadingMore ? t('loading') : t('load_more')) + '</button>';
        }
        pagEl.innerHTML = pagHtml;
      }
    }

    function renderSubtitle() {
      // [data-subtitle] lives in the header band, outside [data-view="list"].
      var el = root.querySelector('[data-subtitle]');
      if (!el) return;
      if (state.customer && (state.customer.name || state.customer.email)) {
        var name = state.customer.name || state.customer.email;
        var html = '<span>' + escHtml(t('signed_in_label')) + '</span> '
          + '<span class="wth-ph__who-badge">' + escHtml(name) + '</span>';
        if (state.customer.email && state.customer.name) {
          html += ' <span class="wth-ph__who-email">' + escHtml(state.customer.email) + '</span>';
        }
        el.innerHTML = html;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    }

    function renderOrderCard(order) {
      var si = statusInfoForSummary(order);
      var itemCount = orderItemCount(order, false);
      var reorderCount = orderItemCount(order, true);
      var summary = itemSummary(order);
      var reorderingThis = state.reordering === order.id;
      var reorderLabel = reorderingThis ? t('reorder_adding')
        : (reorderCount === 1 ? t('reorder_all_items_one') : t('reorder_all_items', { count: reorderCount }));
      var trackable = si.kind === 'pending' || si.kind === 'partial' || si.kind === 'shipped';
      var detailsLabel = trackable ? t('card_details_track') : t('card_details');
      var badge = itemCount > 0 ? '<span class="wth-oh3__order-thumb-badge">' + itemCount + '</span>' : '';
      var pill = '<span class="wth-oh3__pill ' + si.className + '"><span class="wth-oh3__pill-dot"></span>' + escHtml(si.label) + '</span>';
      var mini = si.kind === 'cancelled' ? '' : miniTimelineHtml(si.kind);
      var amtCls = si.kind === 'cancelled' ? ' wth-oh3__order-amt--muted' : '';

      return ''
        + '<div class="wth-oh3__order-card" data-order-card="' + escAttr(order.id) + '">'
        + '<div class="wth-oh3__order-main">'
        + '<div class="wth-oh3__order-thumb">' + SVG.tire + badge + '</div>'
        + '<div class="wth-oh3__order-id">'
        + '<span class="wth-oh3__order-num">' + escHtml(order.name) + '</span>'
        + '<span class="wth-oh3__order-po">' + (order.poNumber ? 'PO ' + escHtml(order.poNumber) : '—') + '</span>'
        + '<span class="wth-oh3__order-date">' + escHtml(formatDate(order.processedAt)) + '</span>'
        + '</div>'
        + '<div class="wth-oh3__order-summary">'
        + '<span class="wth-oh3__order-summary-lead">' + escHtml(summary.lead) + '</span>'
        + (summary.sub ? '<span class="wth-oh3__order-summary-sub">' + escHtml(summary.sub) + '</span>' : '')
        + '</div>'
        + '<div class="wth-oh3__order-status">' + pill + mini + '</div>'
        + '<div class="wth-oh3__order-total"><span class="wth-oh3__order-amt' + amtCls + '">' + escHtml(formatMoney(order.total.amount, order.total.currency)) + '</span></div>'
        + '</div>'
        + '<div class="wth-oh3__order-actions">'
        + '<a class="wth-oh3__order-detail-link" href="#/order/' + encodeURIComponent(order.numericId) + '" aria-label="' + escAttr(detailsLabel + ' ' + order.name) + '">' + escHtml(detailsLabel) + '</a>'
        + '<button type="button" class="wth-oh3__reorder-btn" data-reorder="' + escAttr(order.id) + '" aria-label="' + escAttr(t('action_reorder') + ' ' + order.name) + '"' + (reorderingThis ? ' disabled' : '') + '>'
        + (reorderingThis ? '' : SVG.reorder) + escHtml(reorderLabel) + '</button>'
        + '</div>'
        + '</div>';
    }

    // --- Detail status timeline ---
    /* States reflect only what we KNOW from the order payload: Ordered always done;
       once shipped/fulfilled, Preparing + Shipped are confirmed done; Delivered stays
       upcoming (no delivery timestamp exists in the payload) until live FedEx tracking
       confirms it. The only pulsing "active" step is Preparing while still unfulfilled —
       so a shipped order with no live tracking (e.g. GLS) never looks stuck mid-step. */
    function timelineState(order) {
      var f = (order.displayFulfillmentStatus || '').toUpperCase();
      var hasFulfillment = order.fulfillments && order.fulfillments.length > 0;
      var shippedKind = f === 'FULFILLED' || f === 'PARTIALLY_FULFILLED' || f === 'PARTIAL' || hasFulfillment;
      return {
        ordered: 'done',
        preparing: shippedKind ? 'done' : 'active',
        shipped: shippedKind ? 'done' : 'upcoming',
        delivered: 'upcoming',
      };
    }
    function buildTimelineHtml(order) {
      if (order.cancelledAt) {
        return '<div class="wth-oh3__cancelled-notice">' + SVG.x
          + '<span>' + escHtml(t('tl_cancelled', { date: formatDate(order.cancelledAt) })) + '</span></div>';
      }
      var st = timelineState(order);
      var shippedWhen = '';
      if (order.fulfillments && order.fulfillments.length) {
        var dates = order.fulfillments.map(function (f) { return f.createdAt; }).filter(Boolean).sort();
        if (dates.length) shippedWhen = formatDate(dates[0]);
      }
      var steps = [
        { key: 'ordered', state: st.ordered, label: t('tl_ordered'), when: formatDate(order.processedAt) },
        { key: 'preparing', state: st.preparing, label: t('tl_preparing'), when: st.preparing === 'active' ? t('tl_in_progress') : '' },
        { key: 'shipped', state: st.shipped, label: t('tl_shipped'), when: shippedWhen },
        { key: 'delivered', state: st.delivered, label: t('tl_delivered'), when: '' },
      ];
      var stepsHtml = steps.map(function (s) {
        var cls = s.state === 'done' ? ' is-done' : (s.state === 'active' ? ' is-active' : '');
        var icon = s.state === 'done' ? SVG.check : (STEP_ICON[s.key] || '');
        return '<div class="wth-oh3__tl-step' + cls + '" data-tl-step="' + s.key + '">'
          + '<span class="wth-oh3__tl-line"></span>'
          + '<span class="wth-oh3__tl-dot">' + icon + '</span>'
          + '<span class="wth-oh3__tl-lbl">' + escHtml(s.label) + '</span>'
          + '<span class="wth-oh3__tl-when" data-tl-when="' + s.key + '">' + escHtml(s.when) + '</span>'
          + '</div>';
      }).join('');
      return '<h4 class="wth-oh3__sec-h wth-oh3__tl-heading">' + escHtml(t('tl_heading')) + '</h4>'
        + '<div class="wth-oh3__timeline" data-timeline><div class="wth-oh3__tl">' + stepsHtml + '</div></div>';
    }
    /* Upgrade the detail timeline from live FedEx tracking, AGGREGATED over every
       parcel of the order (one FedEx number per tire is the default shape of a WTH
       order — audit 2026-09-30 #24: a single delivered parcel used to flip the
       order-level step to "Delivered" while the other tires were still in transit).
       Rules:
         - "Delivered" only when EVERY parcel with a live status is delivered
           (dated with the LAST arrival).
         - Some delivered, some not → "Partially delivered" (is-active, N of M).
         - Parcels WITHOUT a live status (GLS / Canada Post — no Track client —, or a
           FedEx lookup that failed / was not found) are excluded from the count:
           they can never confirm anything, so waiting for them would block the step
           forever. They keep their per-parcel line unchanged. A lookup still in
           flight counts as "not yet delivered" so the step never flashes
           "Delivered" before its sibling answers → both response orders converge.
       The order payload has no delivery timestamp, so the step starts "upcoming"
       and is driven entirely from here. Re-applied from scratch on every result
       (NOT monotonic): a later in-transit answer can and must revert "Delivered". */
    var liveTrackingByNumber = {};
    var PENDING_LIVE = { pending: true };
    /* Called before the lookups start: every FedEx number is registered as pending. */
    function beginLiveTracking(numbers) {
      liveTrackingByNumber = {};
      (numbers || []).forEach(function (n) { if (n) liveTrackingByNumber[n] = PENDING_LIVE; });
    }
    /* Pure aggregation of the per-parcel live results. `results` = tracking object,
       PENDING_LIVE (lookup in flight) or null (no live status). */
    function aggregateLiveDelivery(results) {
      var out = { tracked: 0, delivered: 0, pending: 0, lastActual: '', lastEstimate: '', outForDelivery: false };
      Object.keys(results).forEach(function (n) {
        var r = results[n];
        if (r === PENDING_LIVE) { out.pending++; return; }
        if (!r) return;
        out.tracked++;
        if (r.actualDelivery) {
          out.delivered++;
          if (String(r.actualDelivery) > out.lastActual) out.lastActual = String(r.actualDelivery);
          return;
        }
        if (r.estimatedDelivery && String(r.estimatedDelivery) > out.lastEstimate) out.lastEstimate = String(r.estimatedDelivery);
        if ((r.statusCode || '').toUpperCase() === 'OD') out.outForDelivery = true;
      });
      out.allDelivered = out.tracked > 0 && out.pending === 0 && out.delivered === out.tracked;
      out.partial = out.delivered > 0 && !out.allDelivered;
      return out;
    }
    function updateTimelineFromTracking(track, number) {
      if (number) liveTrackingByNumber[number] = track || null;
      var tl = detailView.querySelector('[data-timeline]');
      if (!tl) return;
      var delivered = tl.querySelector('[data-tl-step="delivered"]');
      if (!delivered) return;
      var deliveredWhen = tl.querySelector('[data-tl-when="delivered"]');
      var deliveredLbl = delivered.querySelector('.wth-oh3__tl-lbl');
      var deliveredDot = delivered.querySelector('.wth-oh3__tl-dot');
      var agg = aggregateLiveDelivery(liveTrackingByNumber);
      if (agg.tracked === 0 && agg.pending === 0) return;

      // Reset the Delivered step, then apply the aggregated state.
      delivered.classList.remove('is-done'); delivered.classList.remove('is-active');
      if (deliveredLbl) deliveredLbl.textContent = t('tl_delivered');
      if (deliveredDot) deliveredDot.innerHTML = STEP_ICON.delivered || '';

      if (agg.allDelivered) {
        ['ordered', 'preparing', 'shipped'].forEach(function (k) {
          var el = tl.querySelector('[data-tl-step="' + k + '"]');
          if (el) {
            el.classList.add('is-done'); el.classList.remove('is-active');
            var d = el.querySelector('.wth-oh3__tl-dot'); if (d) d.innerHTML = SVG.check;
          }
        });
        delivered.classList.add('is-done');
        if (deliveredDot) deliveredDot.innerHTML = SVG.check;
        if (deliveredWhen) deliveredWhen.textContent = formatDate(agg.lastActual);
        return;
      }
      if (agg.partial) {
        delivered.classList.add('is-active');
        if (deliveredLbl) deliveredLbl.textContent = t('tl_partially_delivered');
        if (deliveredWhen) {
          var txt = t('tl_parcels_delivered', { done: agg.delivered, total: agg.tracked + agg.pending });
          if (agg.lastEstimate) txt += ' \u00b7 ' + t('tl_delivered_est', { date: formatDate(agg.lastEstimate) });
          deliveredWhen.textContent = txt;
        }
        return;
      }
      if (deliveredWhen) {
        deliveredWhen.textContent = agg.lastEstimate ? t('tl_delivered_est', { date: formatDate(agg.lastEstimate) }) : '';
      }
      // "Out for delivery" → highlight the Delivered step as in-progress.
      if (agg.outForDelivery) delivered.classList.add('is-active');
    }

    // --- Detail render ---
    function renderDetailLoading() {
      detailView.innerHTML = '<div class="wth-oh3__loading">' + escHtml(t('loading_order')) + '</div>';
    }

    function renderDetail() {
      if (state.detailError) {
        detailView.innerHTML = ''
          + '<a href="#" class="wth-oh3__back-link" data-go-list>' + escHtml(t('detail_back')) + '</a>'
          + '<div class="wth-oh3__error-card">' + escHtml(state.detailError) + '</div>';
        return;
      }
      var order = state.detailOrder;
      if (!order) {
        var loginUrl2 = buildLoginUrl();
        detailView.innerHTML = ''
          + '<div class="wth-oh3__empty">'
          + '<h3 class="wth-oh3__heading">' + escHtml(t('please_sign_in_heading')) + '</h3>'
          + '<p>' + escHtml(t('please_sign_in_detail_body')) + '</p>'
          + '<p><a class="wth-oh3__cta-btn" href="' + escAttr(loginUrl2) + '">' + escHtml(t('sign_in_cta')) + '</a></p>'
          + '</div>';
        return;
      }

      var pill = statusInfoForDetail(order);
      var mailSubject = encodeURIComponent(t('return_subject', { name: order.name }));
      var mailBody = encodeURIComponent(t('return_body', { name: order.name }));
      var returnHref = 'mailto:support@wheeltirehub.ca?subject=' + mailSubject + '&body=' + mailBody;

      // Summary card
      var fulfLabel = order.displayFulfillmentStatus ? prettyStatus(order.displayFulfillmentStatus) : '';
      var finLabel = order.displayFinancialStatus ? prettyStatus(order.displayFinancialStatus) : '';
      var statusLine = (fulfLabel && finLabel) ? fulfLabel + ' · ' + finLabel : (fulfLabel || finLabel || '—');
      var poDisplay = order.poNumber ? '<strong>' + escHtml(order.poNumber) + '</strong>' : '<span class="wth-oh3__muted">—</span>';
      var summaryBody = ''
        + '<div><strong>' + escHtml(t('summary_date')) + '</strong> ' + escHtml(formatDate(order.processedAt)) + '</div>'
        + '<div><strong>' + escHtml(t('summary_po')) + '</strong> ' + poDisplay + '</div>'
        + '<div><strong>' + escHtml(t('summary_status')) + '</strong> ' + escHtml(statusLine) + '</div>';

      // Shipping
      var sa = order.shippingAddress;
      var shipBody = '';
      if (sa) {
        var cityLine = [sa.city, sa.provinceCode, sa.zip].filter(function (x) { return !!x; }).join(', ');
        shipBody += (sa.name ? '<div>' + escHtml(sa.name) + '</div>' : '');
        shipBody += (sa.company ? '<div>' + escHtml(sa.company) + '</div>' : '');
        shipBody += (sa.address1 ? '<div>' + escHtml(sa.address1) + '</div>' : '');
        shipBody += (sa.address2 ? '<div>' + escHtml(sa.address2) + '</div>' : '');
        if (cityLine) shipBody += '<div>' + escHtml(cityLine) + '</div>';
        shipBody += (sa.country ? '<div>' + escHtml(sa.country) + '</div>' : '');
        shipBody += (sa.phone ? '<div class="wth-oh3__muted" style="margin-top:4px">' + escHtml(formatPhone(sa.phone)) + '</div>' : '');
      } else { shipBody = '<div>—</div>'; }

      // Customer
      var custBody = ''
        + '<div>' + escHtml((order.customer && (order.customer.displayName || order.customer.email)) || '—') + '</div>'
        + (order.customer && order.customer.email ? '<div class="wth-oh3__muted">' + escHtml(order.customer.email) + '</div>' : '')
        + (order.note ? '<div style="margin-top:8px"><strong>' + escHtml(t('note')) + '</strong> ' + escHtml(order.note) + '</div>' : '');

      // Shipments
      var shipmentsHtml = '';
      if (order.fulfillments && order.fulfillments.length > 0) {
        shipmentsHtml = '<h4 class="wth-oh3__sec-h">' + escHtml(t('shipments_heading')) + '</h4>';
        shipmentsHtml += order.fulfillments.map(function (f) {
          var items = (f.fulfillmentLineItems && f.fulfillmentLineItems.edges) || [];
          var rawStatus = (f.status || '').toUpperCase();
          var statusClass = rawStatus === 'SUCCESS' || rawStatus === 'OPEN' || rawStatus === 'IN_PROGRESS' ? 'wth-oh3__pill-shipped' : 'wth-oh3__pill-pending';
          var statusLabel = prettyFulfillmentStatus(f.status);

          var trackingHtml = '';
          if (f.trackingInfo && f.trackingInfo.length > 0) {
            var trackParts = f.trackingInfo.map(function (ti) {
              if (!ti.company && !ti.number) return '';
              var parts = '';
              if (ti.company) parts += '<strong>' + escHtml(ti.company) + '</strong>';
              if (ti.company && ti.number) parts += ' · ';
              if (ti.number) {
                if (ti.url) parts += '<a href="' + escAttr(ti.url) + '" target="_blank" rel="noreferrer">#' + escHtml(ti.number) + '</a>';
                else parts += '#' + escHtml(ti.number);
              }
              var line = '<div class="wth-oh3__tracking-line"><span class="wth-oh3__tracking-icon" aria-hidden="true">' + SVG.trackLine + '</span><span>' + parts + '</span></div>';
              var isFedex = ti.company && /fedex/i.test(ti.company);
              if (isFedex && ti.number) {
                line += '<div class="wth-oh3__live-status" data-fedex-track="' + escAttr(ti.number) + '"><span class="wth-oh3__live-loading">' + escHtml(t('live_loading')) + '</span></div>';
              }
              return line;
            }).filter(function (x) { return !!x; }).join('');
            if (trackParts) trackingHtml = '<div class="wth-oh3__tracking-row">' + trackParts + '</div>';
          }

          var itemsHtml = items.map(function (e) {
            var it = e.node;
            return liRow(it.lineItem.name, it.lineItem.sku, it.quantity, resolveImageUrl(it.lineItem), '');
          }).join('');

          return ''
            + '<div class="wth-oh3__shipment-card">'
            + '<div class="wth-oh3__shipment-header">'
            + '<div><div class="wth-oh3__shipment-title">' + escHtml(t('shipment_title')) + '</div><div class="wth-oh3__shipment-date">' + escHtml(formatDate(f.createdAt)) + '</div></div>'
            + '<span class="wth-oh3__pill ' + statusClass + '"><span class="wth-oh3__pill-dot"></span>' + escHtml(statusLabel) + '</span>'
            + '</div>'
            + trackingHtml
            + '<div class="wth-oh3__expand-items" style="margin-top:12px">' + itemsHtml + '</div>'
            + '</div>';
        }).join('');
      }

      // Line items
      var lineItemsArr = (order.lineItems && order.lineItems.nodes) || [];
      var lineItemsHtml = '<h4 class="wth-oh3__sec-h">' + escHtml(t('items_count', { count: lineItemsArr.length })) + '</h4>';
      lineItemsHtml += '<div class="wth-oh3__shipment-card"><div class="wth-oh3__expand-items">';
      lineItemsHtml += lineItemsArr.map(function (li) {
        return liRow(li.name, li.sku, li.quantity, resolveImageUrl(li), formatMoneyObj(li.originalTotalSet && li.originalTotalSet.shopMoney));
      }).join('');
      lineItemsHtml += '</div></div>';

      // Totals
      var totalsHtml = ''
        + '<div class="wth-oh3__totals-table">'
        + '<div class="wth-oh3__totals-row"><span>' + escHtml(t('totals_subtotal')) + '</span><span>' + escHtml(formatMoneyObj(order.subtotalPriceSet && order.subtotalPriceSet.shopMoney)) + '</span></div>'
        + '<div class="wth-oh3__totals-row"><span>' + escHtml(t('totals_shipping')) + '</span><span>' + escHtml(formatMoneyObj(order.totalShippingPriceSet && order.totalShippingPriceSet.shopMoney)) + '</span></div>'
        + '<div class="wth-oh3__totals-row"><span>' + escHtml(t('totals_tax')) + '</span><span>' + escHtml(formatMoneyObj(order.totalTaxSet && order.totalTaxSet.shopMoney)) + '</span></div>'
        + '<div class="wth-oh3__totals-row wth-oh3__grand-total"><span>' + escHtml(t('totals_total')) + '</span><span>' + escHtml(formatMoneyObj(order.totalPriceSet && order.totalPriceSet.shopMoney)) + '</span></div>'
        + '</div>';

      var reorderingDetail = state.reordering === order.id;
      // Printable invoice — server-rendered by /apps/wth-admin/orders/<id>/invoice
      // (App Proxy signs the request; the route re-checks ownership). Opens in a
      // new tab so the customer keeps the portal; "download" = browser print → PDF.
      var invoiceHref = '/apps/wth-admin/orders/' + encodeURIComponent(state.detailId)
        + '/invoice?locale=' + encodeURIComponent(i18n.locale || 'en');
      var actionsHtml = ''
        + '<div class="wth-oh3__heading-actions">'
        + '<a href="' + escAttr(returnHref) + '" class="wth-oh3__return-btn">' + escHtml(t('action_return')) + '</a>'
        + '<a href="' + escAttr(invoiceHref) + '" class="wth-oh3__order-detail-link" target="_blank" rel="noopener"'
        + ' aria-label="' + escAttr(t('action_invoice') + ' ' + order.name) + '">'
        + SVG.invoice + escHtml(t('action_invoice')) + '</a>'
        + '<button type="button" class="wth-oh3__reorder-btn" data-reorder-detail="' + escAttr(order.id) + '"' + (reorderingDetail ? ' disabled' : '') + '>'
        + (reorderingDetail ? '' : SVG.reorder) + escHtml(reorderingDetail ? t('reorder_adding') : t('action_reorder')) + '</button>'
        + '</div>';

      detailView.innerHTML = ''
        + '<a href="#" class="wth-oh3__back-link" data-go-list>' + escHtml(t('detail_back')) + '</a>'
        + '<div class="wth-oh3__heading-row">'
        + '<h2 class="wth-oh3__detail-title">' + escHtml(t('detail_heading', { name: order.name })) + ' '
        + '<span class="wth-oh3__pill ' + pill.className + '"><span class="wth-oh3__pill-dot"></span>' + escHtml(pill.label) + '</span></h2>'
        + actionsHtml
        + '</div>'
        + buildTimelineHtml(order)
        + '<div class="wth-oh3__detail-grid">'
        + '<div class="wth-oh3__detail-card"><div class="wth-oh3__detail-card-title">' + escHtml(t('card_summary')) + '</div><div class="wth-oh3__detail-card-body">' + summaryBody + '</div></div>'
        + '<div class="wth-oh3__detail-card"><div class="wth-oh3__detail-card-title">' + escHtml(t('card_shipping_to')) + '</div><div class="wth-oh3__detail-card-body">' + shipBody + '</div></div>'
        + '<div class="wth-oh3__detail-card"><div class="wth-oh3__detail-card-title">' + escHtml(t('card_customer')) + '</div><div class="wth-oh3__detail-card-body">' + custBody + '</div></div>'
        + '</div>'
        + shipmentsHtml
        + lineItemsHtml
        + totalsHtml;

      enrichLiveTracking();
    }

    // --- Live FedEx tracking enrichment ---
    var liveTrackingGen = 0;
    function enrichLiveTracking() {
      var placeholders = detailView.querySelectorAll('[data-fedex-track]');
      var locale = (i18n.locale || 'en');
      var numbers = [];
      placeholders.forEach(function (el) { var n = el.getAttribute('data-fedex-track'); if (n) numbers.push(n); });
      // A re-render (e.g. reorder button state) restarts the aggregation; answers
      // from a previous render must not leak into the new timeline.
      var gen = ++liveTrackingGen;
      beginLiveTracking(numbers);
      placeholders.forEach(function (el) {
        var num = el.getAttribute('data-fedex-track');
        if (!num) return;
        fetch('/apps/wth-admin/tracking?number=' + encodeURIComponent(num) + '&locale=' + encodeURIComponent(locale), { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error('http_' + r.status); return r.json(); })
          .then(function (data) {
            var found = data && data.ok && data.tracking && data.tracking.found;
            el.innerHTML = found ? renderLiveStatus(data.tracking, num) : renderLiveUnavailable();
            if (gen === liveTrackingGen) updateTimelineFromTracking(found ? data.tracking : null, num);
          })
          .catch(function () {
            el.innerHTML = renderLiveUnavailable();
            if (gen === liveTrackingGen) updateTimelineFromTracking(null, num);
          });
      });
    }

    function liveStatusClass(code) {
      switch ((code || '').toUpperCase()) {
        case 'DL': return 'wth-oh3__live-pill--delivered';
        case 'OD': return 'wth-oh3__live-pill--out';
        case 'IT': case 'IN': case 'AR': case 'AF': case 'DP': case 'PU': case 'OC': return 'wth-oh3__live-pill--transit';
        case 'DE': case 'CA': return 'wth-oh3__live-pill--exception';
        default: return 'wth-oh3__live-pill--unknown';
      }
    }
    function renderLiveStatus(track, num) {
      var pillClass = 'wth-oh3__live-pill ' + liveStatusClass(track.statusCode);
      var label = track.statusLabel || (track.statusCode || '—');
      var etaHtml;
      if (track.actualDelivery) etaHtml = '<span class="wth-oh3__live-eta">' + escHtml(t('live_delivered_at')) + ' ' + escHtml(formatDate(track.actualDelivery)) + '</span>';
      else if (track.estimatedDelivery) etaHtml = '<span class="wth-oh3__live-eta">' + escHtml(t('live_eta_label')) + ' ' + escHtml(formatDate(track.estimatedDelivery)) + '</span>';
      else etaHtml = '<span class="wth-oh3__live-eta wth-oh3__muted">' + escHtml(t('live_eta_unknown')) + '</span>';

      var events = track.events || [];
      var eventsHtml = '';
      if (events.length > 0) {
        var rows = events.slice().reverse().map(function (e) {
          return '<li><time>' + escHtml(formatDateTime(e.at)) + '</time><span>' + escHtml(e.description || '') + '</span><em>' + escHtml(e.location || '') + '</em></li>';
        }).join('');
        var detailsId = 'wth-track-events-' + encodeURIComponent(num).replace(/[^a-zA-Z0-9]/g, '');
        eventsHtml = '<details class="wth-oh3__live-events" id="' + detailsId + '"><summary>' + escHtml(t('live_events_show')) + '</summary><ol class="wth-oh3__live-events-list">' + rows + '</ol></details>';
      }
      return '<div class="wth-oh3__live-row"><span class="' + pillClass + '"><span class="wth-oh3__pill-dot"></span>' + escHtml(label) + '</span>' + etaHtml + '</div>' + eventsHtml;
    }
    function renderLiveUnavailable() {
      return '<div class="wth-oh3__live-row wth-oh3__muted">' + escHtml(t('live_unavailable')) + '</div>';
    }

    /* ===== Year in Tires & Wheels (insights) ===== */
    var insightsLoaded = false;
    function maybeLoadInsights() {
      if (insightsLoaded) return;
      var yit = listView.querySelector('[data-yit]');
      if (!yit) return;
      if (state.orders === null) return;
      if (!state.customer) return;
      insightsLoaded = true;
      yit.hidden = false;
      yit.setAttribute('data-loading', 'true');
      fetch('/apps/wth-admin/orders/insights' + (window.location.search || ''), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json().then(function (j) { return { status: res.status, json: j }; }); })
        .then(function (r) {
          if (r.status !== 200 || !r.json || !r.json.signedIn) { yit.hidden = true; return; }
          renderInsights(yit, r.json);
        })
        .catch(function () { yit.hidden = true; });
    }
    function fmtMoney(n) {
      if (n == null || isNaN(n)) return isFrench ? '0 $' : '$0';
      var amt = Math.round(Number(n));
      if (amt >= 100000) {
        var k = Math.round(amt / 100) / 10;
        return isFrench ? String(k).replace('.', ',') + ' k$' : '$' + k + 'k';
      }
      return isFrench ? amt.toLocaleString('fr-CA') + ' $' : '$' + amt.toLocaleString('en-CA');
    }
    function fmtMoneyFull(n) {
      if (n == null || isNaN(n)) return isFrench ? '0 $' : '$0';
      var amt = Math.round(Number(n));
      return isFrench ? amt.toLocaleString('fr-CA') + ' $' : '$' + amt.toLocaleString('en-CA');
    }
    function deltaText(current, previous, suffix) {
      if (!previous || previous === 0) return suffix ? '— ' + suffix : '—';
      var pct = ((current - previous) / previous) * 100;
      var rounded = Math.round(pct);
      var sign = rounded > 0 ? '▲ +' : (rounded < 0 ? '▼ ' : '— ');
      var cls = rounded > 0 ? 'is-up' : (rounded < 0 ? 'is-down' : '');
      return { html: sign + Math.abs(rounded) + '% vs ' + (suffix || ''), cls: cls };
    }
    function setDelta(el, current, previous, prevYearLabel) {
      if (!el) return;
      var d = deltaText(current, previous, prevYearLabel);
      if (typeof d === 'string') { el.textContent = d; el.className = 'wth-yit__stat-delta'; return; }
      el.textContent = d.html; el.className = 'wth-yit__stat-delta ' + d.cls;
    }
    var FINISH_COLORS = { 'Black / Matte': '#1f2937', 'Gunmetal / Anthracite': '#94a3b8', 'Silver / Machined': '#e5e7eb', 'Bronze': '#92400e', 'Other': '#9ca3af' };
    var FINISH_LABELS_FR = { 'Black / Matte': 'Noir / Mat', 'Gunmetal / Anthracite': 'Anthracite', 'Silver / Machined': 'Argent / Usiné', 'Bronze': 'Bronze', 'Other': 'Autre' };

    function renderInsights(yit, data) {
      yit.removeAttribute('data-loading');
      var yearEl = yit.querySelector('[data-yit-year]');
      if (yearEl) yearEl.textContent = String(data.year);

      var prev = data.previous || { tireCount: 0, wheelCount: 0, spent: 0, orderCount: 0 };
      var cur = data.current || { tireCount: 0, wheelCount: 0, spent: 0, orderCount: 0 };

      var subEl = yit.querySelector('[data-yit-toggle-sub]');
      if (subEl) subEl.textContent = t('yit_toggle_sub', { tires: cur.tireCount, wheels: cur.wheelCount });

      yit.querySelector('[data-yit-stat="tires"]').textContent = String(cur.tireCount);
      yit.querySelector('[data-yit-stat="wheels"]').textContent = String(cur.wheelCount);
      yit.querySelector('[data-yit-stat="spent"]').textContent = fmtMoney(cur.spent);
      var avg = cur.orderCount > 0 ? cur.spent / cur.orderCount : 0;
      yit.querySelector('[data-yit-stat="avg"]').textContent = fmtMoney(avg);

      var prevAvg = prev.orderCount > 0 ? prev.spent / prev.orderCount : 0;
      var prevYearLabel = String(data.prevYear);
      setDelta(yit.querySelector('[data-yit-delta="tires"]'), cur.tireCount, prev.tireCount, prevYearLabel);
      setDelta(yit.querySelector('[data-yit-delta="wheels"]'), cur.wheelCount, prev.wheelCount, prevYearLabel);
      setDelta(yit.querySelector('[data-yit-delta="spent"]'), cur.spent, prev.spent, prevYearLabel);
      setDelta(yit.querySelector('[data-yit-delta="avg"]'), avg, prevAvg, prevYearLabel);

      var monthly = data.monthlySpend || [];
      var maxMonth = monthly.reduce(function (m, v) { return v > m ? v : m; }, 0) || 1;
      monthly.forEach(function (v, i) {
        var bar = yit.querySelector('[data-yit-month="' + i + '"]');
        if (!bar) return;
        bar.style.height = Math.max(2, (v / maxMonth) * 100) + '%';
        bar.setAttribute('data-tooltip', fmtMoneyFull(v));
      });

      var tiresUnit = isFrench ? ' pneus' : ' tires';
      var wheelsUnit = isFrench ? ' roues' : ' wheels';
      renderRanked(yit, 'tireBrands', data.topTireBrands, 'count');
      renderRanked(yit, 'tireSizes', data.topTireSizes, 'count', tiresUnit);
      renderRanked(yit, 'wheelBrands', data.topWheelBrands, 'count');
      renderRanked(yit, 'wheelSpecs', data.topWheelSpecs, 'count', wheelsUnit);

      if (data.tiresBySeason) {
        ['winter', 'allSeason', 'summer'].forEach(function (k) {
          var el = yit.querySelector('[data-yit-season-row="' + k + '"]');
          if (el) {
            var totalTires = cur.tireCount || 1;
            var n = data.tiresBySeason[k] || 0;
            el.textContent = Math.round((n / totalTires) * 100) + '% · ' + n + tiresUnit;
          }
        });
      }

      var finishesEl = yit.querySelector('[data-yit-list="wheelFinishes"]');
      if (finishesEl) {
        finishesEl.innerHTML = '';
        (data.wheelFinishes || []).forEach(function (f) {
          var li = document.createElement('li');
          var sw = document.createElement('span'); sw.className = 'wth-yit__finish-swatch'; sw.style.background = FINISH_COLORS[f.name] || '#9ca3af';
          var n = document.createElement('span'); n.className = 'name'; n.textContent = isFrench ? (FINISH_LABELS_FR[f.name] || f.name) : f.name;
          var v = document.createElement('span'); v.className = 'val'; v.textContent = Math.round(f.pct) + '% · ' + f.count + wheelsUnit;
          li.appendChild(sw); li.appendChild(n); li.appendChild(v);
          finishesEl.appendChild(li);
        });
        if (!(data.wheelFinishes && data.wheelFinishes.length)) {
          var empty = document.createElement('li'); empty.className = 'wth-yit__empty'; empty.textContent = t('yit_no_wheels'); finishesEl.appendChild(empty);
        }
      }

      var toggleBtns = yit.querySelectorAll('[data-yit-toggle]');
      Array.prototype.forEach.call(toggleBtns, function (btn) {
        btn.addEventListener('click', function () {
          var which = btn.getAttribute('data-yit-toggle');
          Array.prototype.forEach.call(toggleBtns, function (b) { b.classList.toggle('is-active', b === btn); });
          var panels = yit.querySelectorAll('[data-yit-panel]');
          Array.prototype.forEach.call(panels, function (p) { p.hidden = (p.getAttribute('data-yit-panel') !== which); });
        });
      });
    }
    function renderRanked(yit, key, items, suffix, fallbackUnit) {
      var el = yit.querySelector('[data-yit-list="' + key + '"]');
      if (!el) return;
      el.innerHTML = '';
      if (!items || items.length === 0) {
        var empty = document.createElement('li'); empty.className = 'wth-yit__empty'; empty.textContent = t('yit_no_data'); el.appendChild(empty); return;
      }
      items.forEach(function (item, i) {
        var li = document.createElement('li');
        var rank = document.createElement('span'); rank.className = 'rank'; rank.textContent = String(i + 1);
        var name = document.createElement('span'); name.className = 'name'; name.textContent = item.name;
        var pct = document.createElement('span'); pct.className = 'pct'; pct.textContent = Math.round(item.pct) + '%';
        var amt = document.createElement('span'); amt.className = 'amt'; amt.textContent = item.count + (fallbackUnit || '');
        li.appendChild(rank); li.appendChild(name); li.appendChild(pct); li.appendChild(amt);
        el.appendChild(li);
      });
    }

    // --- Filter input sync + event wiring ---
    function filtersActive() {
      var f = state.filters;
      return !!((f.orderOrPo || '').trim() || (f.productSkuOrName || '').trim() || f.status || f.dateFrom || f.dateTo || f.preset);
    }

    function syncFilterInputs() {
      var inputs = listView.querySelectorAll('[data-filter]');
      inputs.forEach(function (el) {
        var key = el.getAttribute('data-filter');
        if (state.filters[key] !== undefined && document.activeElement !== el) el.value = state.filters[key] || '';
      });
      var sortSel = listView.querySelector('[data-sort]');
      if (sortSel && document.activeElement !== sortSel) sortSel.value = state.sort;
      listView.querySelectorAll('[data-preset]').forEach(function (btn) {
        var on = btn.getAttribute('data-preset') === state.filters.preset;
        btn.classList.toggle('wth-oh3__active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // "Clear filters" only while something is actually filtering (audit E25).
      var clearBtn = listView.querySelector('[data-filters-clear]');
      if (clearBtn) clearBtn.hidden = !filtersActive();
    }

    /* Screen-reader announcement of the matching-order count (audit E25). Only
       when the FILTERS changed since the last render — not on first load, paging,
       sort or "load older" — and debounced so typing in a search box announces
       the settled count once. Written into the [data-results-live] status region. */
    var liveFilterSig = null;
    var liveTimer = null;
    function announceResultCount(count) {
      var sig = JSON.stringify(state.filters);
      if (liveFilterSig === null) { liveFilterSig = sig; return; }
      if (sig === liveFilterSig) return;
      liveFilterSig = sig;
      var liveEl = listView.querySelector('[data-results-live]');
      if (!liveEl) return;
      if (liveTimer) clearTimeout(liveTimer);
      liveTimer = setTimeout(function () {
        liveTimer = null;
        var msg = count === 0 ? t('results_none')
          : count === 1 ? t('results_live_one')
          : t('results_live', { count: count });
        // Empty first so an identical count after a new filter is announced again.
        liveEl.textContent = '';
        setTimeout(function () { liveEl.textContent = msg; }, 50);
      }, 600);
    }

    listView.addEventListener('input', function (e) {
      var el = e.target;
      if (!el || !el.getAttribute) return;
      var fkey = el.getAttribute('data-filter');
      if (fkey && state.filters[fkey] !== undefined) {
        state.filters[fkey] = el.value;
        if (fkey === 'dateFrom' || fkey === 'dateTo') state.filters.preset = '';
        state.currentPage = 1;
        renderList();
      }
    });
    listView.addEventListener('change', function (e) {
      var el = e.target;
      if (!el || !el.getAttribute) return;
      if (el.getAttribute('data-sort') !== null) { state.sort = el.value; renderList(); }
    });
    listView.addEventListener('click', function (e) {
      var target = e.target;
      if (!target) return;

      // Insights collapsible panel toggle
      var panelToggle = target.closest('[data-yit-toggle-panel]');
      if (panelToggle) {
        var yit = panelToggle.closest('[data-yit]');
        var body = yit && yit.querySelector('[data-yit-body]');
        if (body) {
          var willOpen = body.hidden;
          body.hidden = !willOpen;
          panelToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
          yit.classList.toggle('is-open', willOpen);
        }
        return;
      }

      // To-track callout
      if (target.closest('[data-track-go]')) { handleTrackPackages(); return; }

      // Clear every filter (the button only shows while one is active); focus goes
      // back to the first search box since the button hides itself.
      if (target.closest('[data-filters-clear]')) {
        state.filters = { orderOrPo: '', productSkuOrName: '', status: '', dateFrom: '', dateTo: '', preset: '' };
        state.currentPage = 1;
        renderList();
        var firstFilter = listView.querySelector('[data-filter="orderOrPo"]');
        if (firstFilter && firstFilter.focus) firstFilter.focus();
        return;
      }

      // Preset button
      var presetBtn = target.closest('[data-preset]');
      if (presetBtn) {
        var p = presetBtn.getAttribute('data-preset');
        if (state.filters.preset === p) { state.filters.preset = ''; state.filters.dateFrom = ''; state.filters.dateTo = ''; }
        else { state.filters.preset = p; var r = rangeFromPreset(p); state.filters.dateFrom = r.from; state.filters.dateTo = r.to; }
        state.currentPage = 1; renderList(); return;
      }

      if (target.closest('[data-page-prev]')) { if (state.currentPage > 1) { state.currentPage--; renderList(); } return; }
      if (target.closest('[data-page-next]')) { state.currentPage++; renderList(); return; }

      if (target.closest('[data-load-more]')) {
        if (state.loadingMore) return;
        var cursor = state.pageInfo && state.pageInfo.end_cursor;
        if (!cursor) return;
        state.loadingMore = true; renderList();
        fetchOrders(cursor).then(function () {
          state.loadingMore = false;
          if (state.loadMoreError) { state.loadMoreError = false; showToast(t('error_load_orders'), 'error'); }
          renderList();
        });
        return;
      }

      var reorderBtn = target.closest('[data-reorder]');
      if (reorderBtn) { handleReorder(reorderBtn.getAttribute('data-reorder')); return; }
    });

    detailView.addEventListener('click', function (e) {
      var target = e.target;
      if (!target) return;
      if (target.closest('[data-go-list]')) { e.preventDefault(); window.location.hash = ''; return; }
      var reorderBtn = target.closest('[data-reorder-detail]');
      if (reorderBtn) { handleReorder(reorderBtn.getAttribute('data-reorder-detail')); return; }
    });

    function handleReorder(orderGid) {
      if (state.reordering) return;
      state.reordering = orderGid;
      if (!detailView.hidden) renderDetail(); else renderList();

      fetch('/apps/wth-admin/reorder' + (window.location.search || ''), {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId: orderGid }),
      })
        .then(function (resp) { return resp.json(); })
        .then(function (json) {
          state.reordering = null;
          if (!detailView.hidden) renderDetail(); else renderList();
          if (!json.ok || !json.checkoutUrl) { showToast(t('reorder_failed', { message: t('error_unknown') }), 'error'); return; }
          var count = typeof json.item_count === 'number' ? json.item_count : (typeof json.itemCount === 'number' ? json.itemCount : null);
          var msg = count === 1 ? t('reorder_confirm_one') : (count !== null ? t('reorder_confirm', { count: count }) : t('reorder_confirm_generic'));
          var lines = Array.isArray(json.lines) ? json.lines.filter(function (l) { return l && l.id && l.quantity > 0; }) : [];
          if (lines.length) {
            /* Inline bilingual (no locale key owned here): the replay keeps the
               item options, unlike the bare permalink. */
            msg += ' ' + (String(i18n.locale || '').toLowerCase().indexOf('fr') === 0
              ? 'Les options des articles (véhicule, programmation TPMS) sont reprises.'
              : 'Item options (vehicle, TPMS programming) are carried over.');
          }
          if (!window.confirm(msg)) return;
          if (lines.length) replaceCartWithLines(lines, json.checkoutUrl);
          else window.location.href = json.checkoutUrl;
        })
        .catch(function () {
          state.reordering = null;
          if (!detailView.hidden) renderDetail(); else renderList();
          showToast(t('reorder_failed', { message: t('error_network') }), 'error');
        });
    }

    /* Audit 2026-09-30 #04 — a cart permalink can only carry line properties
       for its FIRST product, so replaying `variant:qty` lost `_program` /
       `Programmed for` (TPMS programming → the Cart Transform's +5 $ no longer
       applied) and the `_vehicle_*` / `_hub_bore` / `_lug_spec` /
       `_fitment_placement` keys that drive the cart reminders. Replace the
       cart through /cart/clear.js + ONE /cart/add.js carrying the route's
       allowlisted properties, then land on the cart page (the theme re-injects
       the province + EHF line there, exactly like the permalink path). Any
       failure falls back to the permalink — never a dead end. */
    function replaceCartWithLines(lines, fallbackUrl) {
      var cartPath = (window.location.pathname.split('/pages/')[0] || '') + '/cart';
      var items = lines.map(function (l) {
        return { id: Number(l.id), quantity: Number(l.quantity), properties: l.properties || {} };
      });
      fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } })
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: items }),
          });
        })
        .then(function (resp) {
          if (!resp || !resp.ok) throw new Error('add');
          window.location.href = cartPath;
        })
        .catch(function () { window.location.href = fallbackUrl; });
    }

    // --- Initial route ---
    route();
  });
})();
